import React, { useMemo, useCallback } from 'react';
import { Segmented, ConfigProvider } from 'antd';
import type { SegmentedProps as AntSegmentedProps } from 'antd';
import * as AntdIcons from '@ant-design/icons';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { createCommand } from '../../base/actionParser';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

// Semantic DOM 样式类型
interface SegmentedSemanticStyles {
  root?: React.CSSProperties;
  item?: React.CSSProperties;
  label?: React.CSSProperties;
  icon?: React.CSSProperties;
}

// 选项配置类型
interface SegmentedOption {
  label: string;
  value: string;
  icon?: string;
  disabled?: boolean;
}

export interface AndSegmentedProps extends Omit<AntSegmentedProps, 'options' | 'onChange'>, BaseComponentProps, DataSetConfig {
  /** 选项配置 */
  options?: SegmentedOption[] | string;
  /** 当前值 */
  value?: string;
  /** 默认值 */
  defaultValue?: string;
  /** 是否撑满 */
  block?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 尺寸 */
  size?: 'large' | 'middle' | 'small';
  /** 是否垂直 */
  vertical?: boolean;
  /** 形状 */
  shape?: 'default' | 'round';
  /** 变化动作 */
  changeAction?: string;
  /** 自定义 onChange */
  onChange?: (value: string) => void;
  /** Semantic DOM 样式 */
  styles?: SegmentedSemanticStyles | string;
  /** Design Token */
  token?: Record<string, any> | string;
}

// 解析 JSON
const parseJson = <T,>(value: T | string | undefined, fallback: T): T => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'string') {
    try {
      const trimmed = value.trim();
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        return JSON.parse(trimmed);
      }
    } catch {
      return fallback;
    }
  }
  return value as T;
};

// 获取图标组件
const getIconComponent = (iconName: string): React.ReactNode => {
  if (!iconName) return null;
  const IconComponent = (AntdIcons as any)[iconName];
  if (IconComponent) {
    return <IconComponent />;
  }
  return null;
};

// 解析选项并添加图标
const parseOptions = (options: SegmentedOption[] | string | undefined): AntSegmentedProps['options'] => {
  const items = parseJson(options, [] as SegmentedOption[]);
  return items.map(item => ({
    label: item.label,
    value: item.value,
    icon: item.icon ? getIconComponent(item.icon) : undefined,
    disabled: item.disabled,
  }));
};

export const AndSegmented: React.FC<AndSegmentedProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  // 核心属性
  options: propOptions,
  value: propValue,
  defaultValue,
  block,
  disabled,
  size,
  vertical,
  shape,
  changeAction,
  onChange,
  // 样式
  styles: propStyles,
  token: propToken,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  // 解析样式和 token
  const styles = useMemo(() => parseJson(propStyles, {} as SegmentedSemanticStyles), [propStyles]);
  const token = useMemo(() => parseJson(propToken, {}), [propToken]);

  // 解析选项
  const options = useMemo(() => parseOptions(propOptions), [propOptions]);

  // 从 store 获取 value
  const storeValue = useMemo(() => {
    if (dataSetKey && dataSetField) {
      const value = getDataSet(dataSetKey, dataSetField);
      if (typeof value === 'string') return value;
    }
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  // 计算当前值
  const currentValue = storeValue ?? propValue;

  // 变化处理
  const handleChange = useCallback((value: string | number) => {
    const strValue = String(value);
    
    if (onChange) {
      onChange(strValue);
      return;
    }
    
    // 更新 store
    if (dataSetKey && dataSetField) {
      setDataSet(dataSetKey, dataSetField, strValue);
    }
    
    // 触发动作
    if (changeAction && sessionId) {
      addCommand(sessionId, createCommand(changeAction, { value: strValue }));
    }
  }, [onChange, dataSetKey, dataSetField, setDataSet, changeAction, sessionId, addCommand]);

  // 渲染 Segmented
  const segmentedElement = (
    <Segmented
      {...restProps}
      options={options}
      value={currentValue}
      defaultValue={defaultValue}
      block={block}
      disabled={disabled}
      size={size}
      vertical={vertical}
      shape={shape}
      onChange={handleChange}
      styles={styles}
    />
  );

  // 应用 Design Token
  if (token && Object.keys(token).length > 0) {
    return (
      <ConfigProvider theme={{ components: { Segmented: token } }}>
        {segmentedElement}
      </ConfigProvider>
    );
  }

  return segmentedElement;
};

export default AndSegmented;
